/*
  <ai-prompt-card>: the "Ask AI about this product" bar on product pages (block
  `ai_prompt` in sections/main-product.liquid).

  - Question chips: tap one to open the Saywhatt chat with that question sent.
  - Input: type a question and press Enter (or the send button) to open the chat
    with it sent; an empty send just opens the chat.
  - The input's placeholder types the example questions one after another, so
    the motion costs no height. Reduced-motion users get the static placeholder.

  Saywhatt exposes window.sayWhattChat.toggleChat / sendNewMessage / onReady. If
  the widget never loads, the bar falls back to the link in data-fallback-url.
*/
class AiPromptCard extends HTMLElement {
  constructor() {
    super();
    this.tick = this.tick.bind(this);
    this.pause = this.pause.bind(this);
    this.resume = this.resume.bind(this);
  }

  connectedCallback() {
    this.field = this.querySelector('[data-ai-prompt-field]');
    this.sendButton = this.querySelector('[data-ai-prompt-send]');
    this.chipsRow = this.querySelector('[data-ai-prompt-chips]');
    try {
      this.prompts = JSON.parse(this.dataset.prompts || '[]').filter(Boolean);
    } catch (error) {
      this.prompts = [];
    }
    if (!this.field) return;

    this.staticPlaceholder = this.dataset.placeholder || this.field.getAttribute('placeholder') || '';
    this.holdMs = Math.max(1500, parseInt(this.dataset.interval || '4000', 10));
    this.reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.querySelectorAll('[data-ai-prompt-chip]').forEach((chip) => {
      chip.addEventListener('click', () => this.ask(chip.textContent.trim()));
    });
    if (this.sendButton) this.sendButton.addEventListener('click', () => this.ask(this.field.value.trim()));
    this.field.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.ask(this.field.value.trim());
      }
    });
    this.field.addEventListener('focus', this.pause);
    this.field.addEventListener('blur', this.resume);
    this.field.addEventListener('input', () => {
      if (this.field.value) this.pause();
    });

    if (this.chipsRow) this.updateChipFade();
    if (this.chipsRow) this.chipsRow.addEventListener('scroll', () => this.updateChipFade(), { passive: true });

    if (this.prompts.length && !this.reduceMotion) this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  /* ---------- placeholder typewriter ---------- */

  start() {
    this.stop();
    this.index = 0;
    this.pos = 0;
    this.phase = 'typing';
    this.timer = window.setTimeout(this.tick, 900);
  }

  stop() {
    window.clearTimeout(this.timer);
    this.timer = null;
  }

  pause() {
    this.paused = true;
    this.stop();
    this.field.placeholder = this.staticPlaceholder;
  }

  resume() {
    if (!this.paused || this.field.value || this.reduceMotion) return;
    this.paused = false;
    this.start();
  }

  tick() {
    this.timer = null;
    if (this.paused || document.activeElement === this.field) return;
    const prompt = this.prompts[this.index];

    if (this.phase === 'typing') {
      this.pos += 1;
      this.field.placeholder = prompt.slice(0, this.pos);
      if (this.pos >= prompt.length) {
        this.phase = 'holding';
        this.timer = window.setTimeout(this.tick, this.holdMs);
      } else {
        this.timer = window.setTimeout(this.tick, 26 + Math.random() * 36);
      }
      return;
    }

    if (this.phase === 'holding') {
      this.phase = 'deleting';
      this.timer = window.setTimeout(this.tick, 0);
      return;
    }

    this.pos -= 2;
    if (this.pos <= 0) {
      this.pos = 0;
      this.field.placeholder = '';
      this.index = (this.index + 1) % this.prompts.length;
      this.phase = 'typing';
      this.timer = window.setTimeout(this.tick, 300);
    } else {
      this.field.placeholder = prompt.slice(0, this.pos);
      this.timer = window.setTimeout(this.tick, 12);
    }
  }

  updateChipFade() {
    const row = this.chipsRow;
    const more = row.scrollWidth - row.clientWidth - row.scrollLeft > 4;
    this.classList.toggle('ai-prompt--more', more);
  }

  /* ---------- hand-off to Saywhatt ---------- */

  ask(question) {
    const chat = window.sayWhattChat;
    if (!chat || typeof chat.toggleChat !== 'function') {
      const fallback = this.dataset.fallbackUrl;
      if (fallback) window.location.href = fallback;
      return;
    }
    this.pause();
    chat.toggleChat(true);
    if (question) {
      const send = () => window.setTimeout(() => chat.sendNewMessage(question), 500);
      if (typeof chat.onReady === 'function') chat.onReady(send);
      else send();
      this.field.value = '';
    }
    this.field.blur();
  }
}

if (!customElements.get('ai-prompt-card')) {
  customElements.define('ai-prompt-card', AiPromptCard);
}
